import { useNavigate } from 'react-router-dom';
import { Stack, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { useRewardBudgets } from '@/hooks/useRewards';
import { budgetState } from '@/components/ui/statusHelpers';
import { budgetWindowText, resourceId, usdText } from '@/lib/rewards';
import { BudgetUsage } from '../components/BudgetUsage';

const PAGE_SIZE = 50;
const COLUMNS = ['Budget', 'Status', 'Left', 'Cap per payment', 'Runs'] as const;

export function BudgetsListPage() {
  const navigate = useNavigate();
  const pagination = useCursorPagination(PAGE_SIZE);
  const { data, isLoading, isError } = useRewardBudgets(PAGE_SIZE, pagination.pageToken ?? '');
  const budgets = data?.rewardBudgets ?? [];
  const nextPageToken = data?.nextPageToken ?? '';
  const none = !isLoading && !isError && budgets.length === 0 && !pagination.hasPreviousPage;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          A budget caps how much Pay with LIL may pay back, in total and per payment. Each partner draws from one.
        </Typography>
        <Button startIcon="plus" onClick={() => navigate('/rewards/budgets/new')}>
          New budget
        </Button>
      </Stack>

      {none ? (
        <Card variant="elevated">
          <EmptyState
            icon="wallet"
            title="No budgets yet"
            subtitle="Add one before adding partners: every partner draws from a budget."
            action={{ label: 'New budget', onClick: () => navigate('/rewards/budgets/new') }}
          />
        </Card>
      ) : (
        <Card variant="elevated" padding="none">
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={COLUMNS.length}
              isLoading={isLoading}
              isError={isError}
              isEmpty={budgets.length === 0}
              loadingRows={4}
              emptyMessage="No budgets on this page."
              errorMessage="Could not load budgets."
            >
              {budgets.map((b) => {
                const state = budgetState(b.state);
                return (
                  <TableRow
                    key={b.name}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/rewards/budgets/${resourceId(b.name, 'rewardBudgets')}`)}
                  >
                    <TableCell>{b.displayName}</TableCell>
                    <TableCell>
                      <StatusPill tone={state.tone} label={state.label} size="sm" />
                    </TableCell>
                    <TableCell sx={{ minWidth: 260 }}>
                      <BudgetUsage limitUsd={b.limitUsd} spentUsd={b.spentUsd} reservedUsd={b.reservedUsd} />
                    </TableCell>
                    <TableCell>{usdText(b.maxPaymentUsd)}</TableCell>
                    <TableCell>{budgetWindowText(b.startTime, b.endTime)}</TableCell>
                  </TableRow>
                );
              })}
            </DataTableBody>
          </Table>
        </Card>
      )}

      {!none && (
        <CursorPagination
          firstRowNumber={pagination.firstRowNumber}
          count={budgets.length}
          hasPreviousPage={pagination.hasPreviousPage}
          hasNextPage={!!nextPageToken}
          onPreviousPage={pagination.goToPreviousPage}
          onNextPage={() => pagination.goToNextPage(nextPageToken)}
          disabled={isLoading}
        />
      )}
    </Stack>
  );
}
